import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { motion, useInView } from "framer-motion";
import { ChevronLeft, ChevronRight, Quote, Package, Tag, Store, Truck, ShieldCheck, Leaf, Receipt, Clock, ArrowUpRight } from "lucide-react";
import { Cta } from "@/components/blocks/shared";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Button } from "@/components/ui/button";
import { cmsText, mediaUrl } from "@/lib/cms";
import { Reveal, SectionHeading } from "@/components/blocks/shared";

/* ---------------------------------------------------------------- FAQ */
export const FaqBlock = ({ block }) => {
  const { i18n } = useTranslation();
  const T = (f) => cmsText(f, i18n.language);
  const items = (block.items || []).filter((i) => T(i.vraag));
  // "Hoe bestel ik?" standaard opengeklapt tonen.
  const standaardOpen = items.findIndex((i) => String(T(i.vraag)).toLowerCase().includes("hoe bestel"));
  return (
    <section className="border-b bg-background" data-testid="block-faq">
      <div className="mx-auto grid max-w-7xl gap-10 px-4 py-20 sm:px-6 lg:grid-cols-12 lg:px-8 lg:py-28">
        <Reveal className="lg:col-span-4">
          <div className="lg:sticky lg:top-28">
            <SectionHeading eyebrow={T(block.eyebrow)} title={T(block.titel)} subtitle={T(block.subtitel)} />
          </div>
        </Reveal>
        <Reveal delay={0.1} className="lg:col-span-8">
          <Accordion type="single" collapsible defaultValue={standaardOpen >= 0 ? `faq-${standaardOpen}` : undefined} className="divide-y rounded-2xl border bg-card px-2 sm:px-4">
            {items.map((item, i) => (
              <AccordionItem key={i} value={`faq-${i}`} className="border-0" data-testid={`faq-item-${i}`}>
                <AccordionTrigger className="py-5 text-left font-display text-base font-semibold hover:no-underline sm:text-lg [&>svg]:h-5 [&>svg]:w-5 [&>svg]:text-primary" data-testid={`faq-trigger-${i}`}>
                  <span className="flex items-start gap-4">
                    <span className="font-mono text-xs text-muted-foreground">{String(i + 1).padStart(2, "0")}</span>
                    {T(item.vraag)}
                  </span>
                </AccordionTrigger>
                <AccordionContent className="pb-6 pl-10 text-sm leading-relaxed text-muted-foreground whitespace-pre-line">
                  {T(item.antwoord)}
                  {item.video && (
                    <div className="mt-4 overflow-hidden rounded-2xl border bg-ink" data-testid={`faq-video-${i}`}>
                      <video
                        controls
                        playsInline
                        preload="metadata"
                        poster={mediaUrl(item.poster) || undefined}
                        className="aspect-video w-full bg-ink"
                      >
                        <source src={mediaUrl(item.video)} type="video/mp4" />
                      </video>
                    </div>
                  )}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </Reveal>
      </div>
    </section>
  );
};

/* -------------------------------------------------------------- Stats */
const CountUp = ({ waarde }) => {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: "-40px" });
  const [n, setN] = useState(0);
  const doel = Number(String(waarde).replace(/[^\d.]/g, "")) || 0;
  const isGetal = doel > 0 && /^\d[\d.,]*$/.test(String(waarde).trim());
  useEffect(() => {
    if (!inView || !isGetal) return;
    const start = performance.now();
    const duur = 1200;
    let raf;
    const tick = (now) => {
      const p = Math.min(1, (now - start) / duur);
      setN(Math.round(doel * (1 - Math.pow(1 - p, 3))));
      if (p < 1) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [inView, doel, isGetal]);
  return <span ref={ref}>{isGetal ? String(n) : waarde}</span>;
};

const COLS = { 1: "lg:grid-cols-1", 2: "lg:grid-cols-2", 3: "lg:grid-cols-3", 4: "lg:grid-cols-4" };

export const StatsBlock = ({ block }) => {
  const { i18n } = useTranslation();
  const T = (f) => cmsText(f, i18n.language);
  const items = (block.items || []).filter((i) => i.waarde || i.prefix || i.suffix);
  const donker = block.stijl !== "licht";
  return (
    <section className={`${donker ? "section-ink" : "bg-card border-b"} relative`} data-testid="block-cijfers">
      <div className="mx-auto max-w-7xl px-4 py-14 sm:px-6 lg:px-8">
        <div className={`grid grid-cols-2 gap-px overflow-hidden rounded-2xl ${COLS[Math.min(items.length, 4)] || COLS[4]} ${donker ? "bg-white/10" : "bg-border"}`}>
          {items.map((it, i) => (
            <motion.div
              key={i}
              initial={{ opacity: 0, y: 12 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: i * 0.08 }}
              className={`p-6 sm:p-8 ${donker ? "bg-ink" : "bg-card"}`}
              data-testid={`stat-${i}`}
            >
              <p className="font-display text-3xl font-extrabold tracking-tight sm:text-4xl lg:text-5xl">
                {it.prefix}<CountUp waarde={it.waarde} />{it.suffix}
              </p>
              <p className="mt-2 text-sm text-muted-foreground">{T(it.label)}</p>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

/* ------------------------------------------------------- Testimonials */
const Logo = ({ item }) =>
  item.logo ? (
    <img src={item.logo} alt={item.winkel || ""} className="h-10 max-w-[160px] object-contain object-left" data-testid="testimonial-logo" />
  ) : (
    <span className="font-display text-lg font-extrabold tracking-tight text-foreground" data-testid="testimonial-wordmark">{item.winkel || item.naam}</span>
  );

export const TestimonialsBlock = ({ block }) => {
  const { i18n } = useTranslation();
  const T = (f) => cmsText(f, i18n.language);
  const items = (block.items || []).filter((i) => T(i.quote));
  const ref = useRef(null);
  const scroll = (dir) => ref.current?.scrollBy({ left: dir * (ref.current.clientWidth * 0.8), behavior: "smooth" });
  if (!items.length) return null;
  return (
    <section className="relative overflow-hidden border-b bg-sand-light" data-testid="block-testimonials">
      <div className="mx-auto max-w-7xl px-4 py-20 sm:px-6 lg:px-8 lg:py-28">
        <Reveal className="flex flex-wrap items-end justify-between gap-6">
          <SectionHeading eyebrow={T(block.eyebrow)} title={T(block.titel)} subtitle={T(block.subtitel)} />
          {items.length > 3 && (
            <div className="flex gap-2">
              <Button variant="outline" size="icon" onClick={() => scroll(-1)} aria-label="Vorige" data-testid="testimonials-prev"><ChevronLeft className="h-4 w-4" /></Button>
              <Button variant="outline" size="icon" onClick={() => scroll(1)} aria-label="Volgende" data-testid="testimonials-next"><ChevronRight className="h-4 w-4" /></Button>
            </div>
          )}
        </Reveal>
        <div ref={ref} className="scrollbar-none -mx-4 mt-12 flex snap-x snap-mandatory gap-5 overflow-x-auto px-4 pb-4 sm:mx-0 sm:px-0">
          {items.map((it, i) => (
            <motion.figure
              key={i}
              initial={{ opacity: 0, y: 16 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: i * 0.06 }}
              className="card-lift relative flex w-[85%] shrink-0 snap-start flex-col rounded-2xl border bg-card p-7 sm:w-[46%] lg:w-[31%]"
              data-testid={`testimonial-${i}`}
            >
              <div className="flex items-start justify-between gap-4 border-b pb-5">
                <Logo item={it} />
                <Quote className="h-5 w-5 shrink-0 text-primary/40" aria-hidden="true" />
              </div>
              <blockquote className="mt-5 flex-1 font-serif text-lg leading-relaxed text-foreground sm:text-xl">{T(it.quote)}</blockquote>
              <figcaption className="mt-6 flex items-end justify-between gap-3">
                <div>
                  <p className="text-sm font-semibold">{it.naam}</p>
                  <p className="text-xs text-muted-foreground">{[it.functie, it.winkel, it.plaats].filter(Boolean).join(" · ")}</p>
                </div>
                {it.link && (
                  <a href={it.link} target="_blank" rel="noreferrer" className="text-muted-foreground transition-colors hover:text-primary" aria-label={it.winkel} data-testid={`testimonial-link-${i}`}>
                    <ArrowUpRight className="h-4 w-4" />
                  </a>
                )}
              </figcaption>
            </motion.figure>
          ))}
        </div>
      </div>
    </section>
  );
};

/* ----------------------------------------------------------- Benefits */
const ICONS = { package: Package, tag: Tag, store: Store, truck: Truck, shield: ShieldCheck, leaf: Leaf, receipt: Receipt, clock: Clock };

export const BenefitsBlock = ({ block }) => {
  const { i18n } = useTranslation();
  const T = (f) => cmsText(f, i18n.language);
  const items = (block.items || []).filter((i) => T(i.titel));
  return (
    <section className="section-ink relative" data-testid="block-voordelen">
      <div className="relative mx-auto grid max-w-7xl gap-12 px-4 py-20 sm:px-6 lg:grid-cols-12 lg:gap-16 lg:px-8 lg:py-28">
        <Reveal className="lg:col-span-4">
          <div className="lg:sticky lg:top-28">
            <SectionHeading eyebrow={T(block.eyebrow)} title={T(block.titel)} subtitle={T(block.subtitel)} dark />
            <div className="mt-8">
              <Cta label={T(block.cta_label)} link={block.cta_link} testId="block-voordelen-cta" arrow className="bg-ink-foreground text-ink hover:bg-white" />
            </div>
          </div>
        </Reveal>
        <div className="grid gap-x-12 gap-y-8 sm:grid-cols-2 lg:col-span-8">
          {items.map((it, i) => {
            const Icon = ICONS[it.icoon] || Package;
            return (
              <Reveal key={i} delay={i * 0.07}>
                <div className="flex h-full gap-4 border-t border-white/10 pt-5" data-testid={`voordeel-${i}`}>
                  <Icon className="mt-0.5 h-5 w-5 shrink-0 text-[hsl(178_55%_62%)]" aria-hidden="true" />
                  <div>
                    <h3 className="font-display text-base font-semibold tracking-tight text-ink-foreground">{T(it.titel)}</h3>
                    <p className="mt-1.5 text-sm leading-relaxed text-ink-foreground/60">{T(it.tekst)}</p>
                  </div>
                </div>
              </Reveal>
            );
          })}
        </div>
      </div>
    </section>
  );
};
